import { useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { Link } from 'react-router';
import { matchesWords, pacificStamp, SEARCH_WORDS, searchWordsOf, type FailureKey, type ReadyDay, type Trends, type TrendDay } from '@claims/core';
import { countOf } from './count.js';
import { api } from './api.js';
import { FRAME_SEARCH, Shell, Topbar } from './Shell.js';
import { QueryState } from './states.js';

// The Trends page (Cesar 2026-10-13 in the team DM, Rod "1a"; aged claim numbers: Neila 10-13): the last 30 days, view-only. Every number
// reuses the rule another screen shows (core trends.ts), so this page never disagrees with the Matters tile, the Today page, Open issues or
// the 5:30 pm email. Charts follow the console's tokens: one series color per kind of entry, a legend, a hover box, and a table view.

const SERIES = [
  { key: 'ai', label: 'AI call', cls: 'tr-s-ai' },
  { key: 'handoff', label: 'AI reached the rep, staff finished', cls: 'tr-s-hand' },
  { key: 'afterAi', label: 'Staff, after an AI miss', cls: 'tr-s-after' },
  { key: 'team', label: 'Staff, no AI call', cls: 'tr-s-team' },
  { key: 'bySystem', label: 'Policy field', cls: 'tr-s-sys' },
] as const;
type SeriesKey = (typeof SERIES)[number]['key'];
const pct = (n: number, d: number) => (d ? `${Math.round((100 * n) / d)}%` : '–');
const sum = (xs: number[]) => xs.reduce((a, b) => a + b, 0);
const daysWord = (n: number) => (n < 1 ? 'under a day' : `${n % 1 ? n.toFixed(1) : n} day${n === 1 ? '' : 's'}`);

/** A single-series line of daily counts (no legend needed: the row names it). */
function Spark({ values, label }: { values: number[]; label: string }) {
  const max = Math.max(1, ...values);
  const w = 120, h = 22;
  const pts = values.map((v, i) => `${(i * w) / Math.max(1, values.length - 1)},${h - 2 - (v / max) * (h - 4)}`).join(' ');
  return (
    <svg className="tr-spark" viewBox={`0 0 ${w} ${h}`} width={w} height={h} role="img" aria-label={`${label}: ${values.join(', ')} a day`}>
      <title>{`${label}, last ${values.length} days: ${values.join(' · ')}`}</title>
      <polyline points={pts} fill="none" strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
    </svg>
  );
}

export function DayTip({ d, goal }: { d: TrendDay; goal: number }) {
  const a = d.added, c = d.calls, r = d.records;
  const n = (v: number | null | undefined) => (v === null || v === undefined ? '–' : countOf(v));
  return (
    <div className="tr-tip" role="tooltip">
      <b>{d.label}{d.partial ? ' (so far)' : ''}</b>
      {a ? <>
        {SERIES.map((s) => <span key={s.key} className="tr-tip-r"><i className={`tr-sw ${s.cls}`} /><span>{s.label}</span><em>{n(a[s.key as SeriesKey])}</em></span>)}
        <span className="tr-tip-r tot"><span>Total · {pct(a.total, goal)} of goal</span><em>{countOf(a.total)}</em></span>
      </> : <span>Not available</span>}
      {d.aged ? <span className="tr-tip-r mt"><span>{countOf(d.aged.d8to30)} came in 8-30 days after the case opened · {countOf(d.aged.over30)} more than 30 days after</span></span> : null}
      <hr />
      {r ? <span className="tr-tip-r"><span>New records (intake + staff)</span><em>{countOf(r.intake)} + {countOf(r.staff)}</em></span> : <span className="tr-tip-r"><span>New records: not available</span></span>}
      {c ? <>
        <span className="tr-tip-r"><span>Calls placed · hit rate</span><em>{countOf(c.placed)} · {c.hitRate === null ? '–' : `${c.hitRate}%`}</em></span>
        {c.unsent ? <span className="tr-tip-r warn"><span>Never went out</span><em>{countOf(c.unsent)}{c.unsentNotRetried ? ` (${countOf(c.unsentNotRetried)} not retried)` : ''}</em></span> : null}
      </> : <span className="tr-tip-r"><span>Calls: not available</span></span>}
    </div>
  );
}

function DayChart({ t }: { t: Trends }) {
  const [hover, setHover] = useState<number | null>(null);
  const max = Math.max(t.goal, ...t.days.map((d) => d.added?.total ?? 0));
  const top = Math.ceil((max * 1.08) / 50) * 50;
  const ticks = [0, ...Array.from({ length: Math.floor(top / 50) }, (_, i) => (i + 1) * 50)];
  const H = 180;
  return (
    <div className="tr-chart" onMouseLeave={() => setHover(null)}>
      <div className="tr-axis" aria-hidden="true">{ticks.slice().reverse().map((v) => <span key={v} style={{ bottom: `${(v / top) * H}px` }}>{v}</span>)}</div>
      <div className="tr-plot" style={{ height: H }}>
        {ticks.map((v) => <i key={v} className="tr-grid" style={{ bottom: `${(v / top) * H}px` }} />)}
        <i className="tr-goal" style={{ bottom: `${(t.goal / top) * H}px` }} title={`goal ${t.goal} a day`}><span>goal {t.goal}</span></i>
        <div className="tr-cols">
          {t.days.map((d, i) => {
            const a = d.added;
            return (
              <button key={d.day} type="button" className={`tr-col${d.weekend ? ' we' : ''}${d.partial ? ' part' : ''}`} onMouseEnter={() => setHover(i)} onFocus={() => setHover(i)}
                onBlur={() => setHover(null)} aria-label={a ? `${d.label}: ${a.total} claim numbers added` : `${d.label}: not available`}>
                <span className="tr-stack" style={{ height: a ? `${(a.total / top) * H}px` : 0 }}>
                  {a ? SERIES.map((s) => (a[s.key as SeriesKey] ? <i key={s.key} className={s.cls} style={{ flexGrow: a[s.key as SeriesKey] ?? 0 }} /> : null)) : null}
                </span>
                {hover === i ? <DayTip d={d} goal={t.goal} /> : null}
              </button>
            );
          })}
        </div>
      </div>
      <div className="tr-x" aria-hidden="true">
        {t.days.map((d, i) => (i % 5 === 4 || i === t.days.length - 1 ? (
          <span key={d.day} className={i === t.days.length - 1 ? 'last' : ''} style={i === t.days.length - 1 ? undefined : { left: `${((i + 0.5) * 100) / t.days.length}%` }}>
            {d.label.replace(/^\w+ /, '')}</span>) : null))}
      </div>
    </div>
  );
}

function Legend() {
  return <div className="tr-legend">{SERIES.map((s) => <span key={s.key}><i className={`tr-sw ${s.cls}`} />{s.label}</span>)}</div>;
}

export function AddedSection({ t }: { t: Trends }) {
  const [table, setTable] = useState(false);
  const known = t.days.filter((d) => d.added);
  const done = t.days.filter((d) => !d.partial);
  const yesterday = done[done.length - 1];
  const w = t.week;
  const change = w && w.lastWeek ? Math.round((100 * (w.thisWeek - w.lastWeek)) / w.lastWeek) : null;
  // today's summary line, moved to the Table tab (Cesar 10-22): the goal is a weekday goal, averaged over the last five full weekdays
  const weekdays = known.filter((d) => !d.partial && !d.weekend).slice(-5);
  const avg = weekdays.length ? Math.round(sum(weekdays.map((d) => d.added!.total)) / weekdays.length) : null;
  const total = sum(known.map((d) => d.added!.total));
  return (
    <section className="tr-card sh-card" aria-labelledby="tr-added">
      <div className="sh-head">
        <div className="sh-title"><h2 id="tr-added" className="sh-t">Claim numbers added</h2><p className="sh-d">Per day, all cases. Goal {t.goal}.</p></div>
        <div className="sh-kpi"><span className="sh-kl">{w && w.to !== yesterday?.label ? `From AI, ${w.from} to ${w.to}` : 'From AI this week'}</span><span className="sh-kv">{w ? countOf(w.thisWeek) : '–'}</span></div>
        <div className="sh-kpi"><span className="sh-kl">Hit rate yesterday</span><span className="sh-kv">{yesterday?.calls?.hitRate === null || !yesterday?.calls ? '–' : `${yesterday.calls.hitRate}%`}</span></div>
        <div className="sh-tabs" role="tablist">
          <button type="button" role="tab" aria-selected={!table} onClick={() => setTable(false)}>Chart</button>
          <button type="button" role="tab" aria-selected={table} onClick={() => setTable(true)}>Table</button>
        </div>
      </div>
      <div className="sh-body">
        {known.length ? (table ? <>
          <div className="tr-sum">
            {avg !== null ? <span><b>{countOf(avg)}</b> a weekday over the last {weekdays.length} full weekdays · goal {t.goal} · {pct(avg, t.goal)}</span> : null}
            <span><b>{countOf(total)}</b> in {known.length} days</span>
            <span className="mt" title="A claim number first entered on a client's own (PLT) or at-fault (DEF) record that day, as Salesforce stands now. It counts numbers added, not proof the claim is new. Second clients from the same crash are not counted.">Same rule as the Matters tile and the 5:30 pm email</span>
          </div>
          <AddedTable t={t} />
        </> : <><DayChart t={t} /><Legend /></>) : <div className="tr-na">Not available right now.</div>}
      </div>
      {w && change !== null ? (
        <div className="sh-foot">
          <div className="sh-fa">{change >= 0 ? 'Up' : 'Down'} {Math.abs(change)}% on the same days last week</div>
          <div className="sh-fb">{countOf(w.thisWeek)} from AI {w.from} to {w.to}, against {countOf(w.lastWeek)}. Hover a day to see what drove it.</div>
        </div>
      ) : null}
      <AgedBlock t={t} />
    </section>
  );
}

/** The Today page for a day (and a filter): the partial (today) day is Today itself, so it carries no `day`. */
export function todayHref(day: string, f: { result?: 'miss' | 'notsent'; reason?: FailureKey } = {}, partial = false): string {
  const q = [partial ? '' : `day=${day}`, f.result ? `result=${f.result}` : '', f.reason ? `reason=${f.reason}` : ''].filter(Boolean).join('&');
  return q ? `/today?${q}` : '/today';
}

export function AddedTable({ t }: { t: Trends }) {
  const n = (v: number | null | undefined) => (v === null || v === undefined ? '–' : countOf(v));
  return (
    <div className="tr-tw">
      <table className="tr-table">
        <thead><tr><th>Day</th><th className="n">Added</th>{SERIES.map((s) => <th key={s.key} className="n" title={s.label}><i className={`tr-sw ${s.cls}`} /></th>)}
          <th className="n">New: intake</th><th className="n">New: staff</th><th className="n">Calls requested</th><th className="n">Placed</th>
          <th className="n">Got a claim #</th><th className="n">Hit rate</th><th className="n">Never went out</th>
          <th className="n">8-30 days after</th><th className="n">More than 30 days after</th></tr></thead>
        <tbody>
          {t.days.slice().reverse().map((d) => (
            <tr key={d.day} className={d.weekend ? 'we' : ''}>
              <td>{d.label}{d.partial ? ' (so far)' : ''}</td>
              <td className="n"><b>{d.added ? countOf(d.added.total) : '–'}</b></td>
              {SERIES.map((s) => <td key={s.key} className="n">{d.added ? n(d.added[s.key as SeriesKey]) : '–'}</td>)}
              <td className="n">{n(d.records?.intake)}</td><td className="n">{n(d.records?.staff)}</td>
              {([d.calls?.requested, d.calls?.placed, d.calls?.claims] as const).map((v, i) => (
                <td key={i} className="n">{v === null || v === undefined ? '–' : <Link to={todayHref(d.day, {}, d.partial)}>{countOf(v)}</Link>}</td>))}
              <td className="n">{d.calls?.hitRate === null || !d.calls ? '–' : `${d.calls.hitRate}%`}</td>
              <td className={`n${d.calls?.unsent ? ' warn' : ''}`}>{d.calls ? (() => {
                const label = `${countOf(d.calls.unsent)}${d.calls.unsentNotRetried ? ` (${countOf(d.calls.unsentNotRetried)} not retried)` : ''}`;
                return d.calls.unsent ? <Link to={todayHref(d.day, { result: 'notsent' }, d.partial)}>{label}</Link> : label;
              })() : '–'}</td>
              <td className="n">{d.aged ? countOf(d.aged.d8to30) : '–'}</td><td className="n">{d.aged ? countOf(d.aged.over30) : '–'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function AgedBlock({ t }: { t: Trends }) {
  const a = t.aged;
  const last7 = t.days.slice(-7).filter((d) => d.aged && d.added);
  const s7 = { total: sum(last7.map((d) => d.added!.total)), mid: sum(last7.map((d) => d.aged!.d8to30)), old: sum(last7.map((d) => d.aged!.over30)) };
  return (
    <div className="tr-aged">
      <h3 className="sc-h">Claim numbers that came in late</h3>
      {a ? (
        <table className="tr-table compact">
          <thead><tr><th /><th className="n">Last 7 days</th><th className="n">Last 30 days</th></tr></thead>
          <tbody>
            <tr><td>Claim numbers added 8-30 days after the case opened</td><td className="n"><b>{countOf(s7.mid)}</b> <span className="mt">{pct(s7.mid, s7.total)}</span></td>
              <td className="n"><b>{countOf(a.d8to30)}</b> <span className="mt">{pct(a.d8to30, a.total)}</span></td></tr>
            <tr><td>Claim numbers added more than 30 days after the case opened</td><td className="n"><b>{countOf(s7.old)}</b> <span className="mt">{pct(s7.old, s7.total)}</span></td>
              <td className="n"><b>{countOf(a.over30)}</b> <span className="mt">{pct(a.over30, a.total)}</span></td></tr>
          </tbody>
        </table>
      ) : <div className="tr-na">Not available right now.</div>}
      {a ? <div className="tr-note mt">Case age = Pacific days from the case's Open Date ({a.byOpenDate} of {a.total}){a.byCreatedDate ? `, or when the case was created when it has none (${a.byCreatedDate})` : ''}{a.unknown ? `; ${a.unknown} with neither date ${a.unknown === 1 ? 'is' : 'are'} left out` : ''}. Exactly 7 days counts as within 7; exactly 30 as 8-30.</div> : null}
      {t.backlog ? (
        <div className="tr-backlog">
          <span className="tr-bl-h">A different number, today's backlog:</span> open auto cases with insurance but <b>no claim number yet</b>, opened in the last {t.backlog.windowDays} days:
          {' '}<b>{countOf(t.backlog.over7)}</b> older than 7 days · <b>{countOf(t.backlog.over30)}</b> older than 30 days
        </div>
      ) : null}
    </div>
  );
}

// ---- Ready to open vs opened (Cesar 2026-10-20): are we making a dent in what is ready, and why the rest waits. Two charts, one axis each
// (the daily moves are tens, the pool is hundreds): the moves per day, then the pool at each day's end. Amber = ready, blue = claim numbers.
const RSERIES = [
  { key: 'became', label: 'Became ready to open', cls: 'tr-s-ready' },
  { key: 'added', label: 'Claim numbers added (the 5:30 pm email’s count)', cls: 'tr-s-after' },
] as const;
const signed = (n: number) => (n > 0 ? `+${countOf(n)}` : n < 0 ? `−${countOf(-n)}` : '0');
const less = (n: number) => (n ? `−${countOf(n)}` : '0');

function ReadyTip({ d, r }: { d: TrendDay; r: ReadyDay }) {
  return (
    <div className="tr-tip" role="tooltip">
      <b>{d.label}{d.partial ? ' (so far)' : ''}</b>
      <span className="tr-tip-r">{countOf(r.start)} ready at the start of the day</span>
      <span className="tr-tip-r"><i className="tr-sw tr-s-ready" />+{countOf(r.became)} became ready</span>
      <span className="tr-tip-r">{less(r.opened)} opened from the ready pool</span>
      <span className="tr-tip-r">{less(r.covered)} out because another record of the same side got its claim number</span>
      <span className="tr-tip-r">{less(r.left)} left without a claim number (case closed, insurer or policy removed)</span>
      <span className="tr-tip-n">{countOf(r.end)} ready at the {d.partial ? 'moment' : 'end of the day'}</span>
      <span className="tr-tip-r mt"><i className="tr-sw tr-s-after" />{d.added ? countOf(d.added.total) : '–'} claim numbers added that day, all records</span>
    </div>
  );
}

function ReadyFlowChart({ t }: { t: Trends }) {
  const [hover, setHover] = useState<number | null>(null);
  const r = t.ready!;
  const max = Math.max(1, ...r.days.map((x) => x.became), ...t.days.map((d) => d.added?.total ?? 0));
  const step = max > 200 ? 100 : max > 80 ? 50 : max > 30 ? 20 : 10;
  const top = Math.ceil((max * 1.08) / step) * step;
  const ticks = Array.from({ length: top / step + 1 }, (_, i) => i * step);
  const H = 150;
  return (
    <div className="tr-chart" onMouseLeave={() => setHover(null)}>
      <div className="tr-axis" aria-hidden="true">{ticks.slice().reverse().map((v) => <span key={v} style={{ bottom: `${(v / top) * H}px` }}>{v}</span>)}</div>
      <div className="tr-plot" style={{ height: H }}>
        {ticks.map((v) => <i key={v} className="tr-grid" style={{ bottom: `${(v / top) * H}px` }} />)}
        <div className="tr-cols">
          {t.days.map((d, i) => {
            const x = r.days[i]!;
            const added = d.added?.total ?? 0;
            return (
              <button key={d.day} type="button" className={`tr-col${d.weekend ? ' we' : ''}${d.partial ? ' part' : ''}`} onMouseEnter={() => setHover(i)} onFocus={() => setHover(i)}
                onBlur={() => setHover(null)} aria-label={`${d.label}: ${x.became} became ready, ${d.added ? added : 'unknown'} claim numbers added, ${x.end} ready at the end`}>
                <span className="tr-pair">
                  <i className="tr-s-ready" style={{ height: `${(x.became / top) * H}px` }} />
                  <i className="tr-s-after" style={{ height: `${(added / top) * H}px` }} />
                </span>
                {hover === i ? <ReadyTip d={d} r={x} /> : null}
              </button>
            );
          })}
        </div>
      </div>
      <DayLabels t={t} />
    </div>
  );
}

function PoolChart({ t }: { t: Trends }) {
  const [hover, setHover] = useState<number | null>(null);
  const r = t.ready!;
  const vals = r.days.map((x) => x.end);
  // a zoomed axis (the change is the point); its two labels say where it starts and ends
  const lo0 = Math.min(...vals, r.days[0]!.start), hi0 = Math.max(...vals, r.days[0]!.start);
  const pad = Math.max(5, Math.round((hi0 - lo0) * 0.15));
  const lo = Math.max(0, lo0 - pad), hi = hi0 + pad;
  const H = 90, W = 1000;
  const y = (v: number) => H - ((v - lo) / Math.max(1, hi - lo)) * H;
  const x = (i: number) => ((i + 0.5) * W) / vals.length;
  return (
    <div className="tr-chart" onMouseLeave={() => setHover(null)}>
      <div className="tr-axis" aria-hidden="true"><span style={{ bottom: `${H}px` }}>{hi}</span><span style={{ bottom: 0 }}>{lo}</span></div>
      <div className="tr-plot" style={{ height: H }}>
        <i className="tr-grid" style={{ bottom: `${H}px` }} /><i className="tr-grid" style={{ bottom: 0 }} />
        <svg className="tr-pool" viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" width="100%" height={H} aria-hidden="true">
          <polyline points={vals.map((v, i) => `${x(i)},${y(v)}`).join(' ')} fill="none" strokeWidth="2" vectorEffect="non-scaling-stroke" strokeLinejoin="round" />
        </svg>
        {hover !== null ? <i className="tr-dot" style={{ left: `${((hover + 0.5) * 100) / vals.length}%`, bottom: `${H - y(vals[hover]!)}px` }} /> : null}
        <div className="tr-cols">
          {t.days.map((d, i) => (
            <button key={d.day} type="button" className="tr-col" onMouseEnter={() => setHover(i)} onFocus={() => setHover(i)} onBlur={() => setHover(null)}
              aria-label={`${d.label}: ${vals[i]} ready at the end of the day`}>
              {hover === i ? <ReadyTip d={d} r={r.days[i]!} /> : null}
            </button>
          ))}
        </div>
      </div>
      <DayLabels t={t} />
    </div>
  );
}

function DayLabels({ t }: { t: Trends }) {
  return (
    <div className="tr-x" aria-hidden="true">
      {t.days.map((d, i) => (i % 5 === 4 || i === t.days.length - 1 ? (
        <span key={d.day} className={i === t.days.length - 1 ? 'last' : ''} style={i === t.days.length - 1 ? undefined : { left: `${((i + 0.5) * 100) / t.days.length}%` }}>
          {d.label.replace(/^\w+ /, '')}</span>) : null))}
    </div>
  );
}

function ReadyTable({ t }: { t: Trends }) {
  const r = t.ready!;
  return (
    <div className="tr-tw">
      <table className="tr-table">
        <thead><tr><th>Day</th><th className="n">Ready at start</th><th className="n">Became ready</th><th className="n">Opened from the pool</th>
          <th className="n">Its side opened on another record</th><th className="n">Left without a claim</th><th className="n">Ready at end</th><th className="n">Claim numbers added</th></tr></thead>
        <tbody>
          {t.days.map((d, i) => ({ d, x: r.days[i]! })).reverse().map(({ d, x }) => (
            <tr key={d.day} className={d.weekend ? 'we' : ''}>
              <td>{d.label}{d.partial ? ' (so far)' : ''}</td><td className="n">{countOf(x.start)}</td><td className="n">{countOf(x.became)}</td><td className="n">{countOf(x.opened)}</td>
              <td className="n">{countOf(x.covered)}</td><td className="n">{countOf(x.left)}</td><td className="n"><b>{countOf(x.end)}</b></td><td className="n">{d.added ? countOf(d.added.total) : '–'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function ReadySection({ t }: { t: Trends }) {
  const [table, setTable] = useState(false);
  const r = t.ready;
  // the last seven full days (today's partial day would pull every average down)
  const full = t.days.map((d, i) => ({ d, x: r?.days[i] })).filter((p) => !p.d.partial && p.x).slice(-7);
  const avg = (xs: number[]) => (xs.length ? sum(xs) / xs.length : 0);
  const became7 = avg(full.map((p) => p.x!.became)), added7 = avg(full.filter((p) => p.d.added).map((p) => p.d.added!.total));
  // the pool's change over those days as a total, never a rate: a weekend adds ready records and opens almost none, so a week's rate misleads
  const net7 = sum(full.map((p) => p.x!.end - p.x!.start));
  const change = r ? r.now - r.days[0]!.start : 0;
  const reasonMax = Math.max(1, ...(r?.reasons ?? []).map((x) => x.n));
  return (
    <section className="tcard tr-card" aria-labelledby="tr-ready">
      <div className="tr-h">
        <h2 id="tr-ready" className="sc-h">Ready to open vs opened, per day</h2>
        {r ? <button type="button" className="tr-tbtn" aria-pressed={table} onClick={() => setTable(!table)}>{table ? 'Show the chart' : 'Show as a table'}</button> : null}
      </div>
      {r ? (
        <>
          <div className="tr-sum">
            <span><b>{countOf(r.now)}</b> ready to open now</span>
            <span><b>{signed(change)}</b> since {t.days[0]!.label}</span>
            {full.length ? <span>last {full.length} full days: <b>{became7.toFixed(0)}</b> became ready a day · <b>{added7.toFixed(0)}</b> claim numbers added a day · the pool <b>{signed(net7)}</b> over those days</span> : null}
          </div>
          {table ? <ReadyTable t={t} /> : (
            <>
              <ReadyFlowChart t={t} />
              <div className="tr-legend">{RSERIES.map((s) => <span key={s.key}><i className={`tr-sw ${s.cls}`} />{s.label}</span>)}</div>
              <h3 className="tr-sub tr-pool-h">Ready to open at the end of each day</h3>
              <PoolChart t={t} />
            </>
          )}
          <div className="tr-two tr-ready-why">
            <div>
              <h3 className="tr-sub">Why ready claims are still unopened, now</h3>
              {r.reasons.length ? <table className="tr-table compact"><tbody>{r.reasons.map((x) => (
                <tr key={x.key}><td>{x.label}</td><td className="n"><b>{countOf(x.n)}</b> <span className="mt">{pct(x.n, r.now)}</span></td>
                  <td className="tr-barc"><i className="tr-bar tr-s-ready" style={{ width: `${(100 * x.n) / reasonMax}%` }} /></td></tr>
              ))}</tbody></table> : <div className="tr-na">Nothing is ready and unopened.</div>}
              <div className="tr-note mt">Ready for: <b>{countOf(r.waiting.d1)}</b> under a day · <b>{countOf(r.waiting.d7)}</b> 2-7 days · <b>{countOf(r.waiting.d30)}</b> 8-30 days · <b>{countOf(r.waiting.older)}</b> since before {t.days[0]!.label}</div>
            </div>
            <div>
              <h3 className="tr-sub">Unopened but not ready: the first thing that keeps each out</h3>
              <table className="tr-table compact"><tbody>
                <tr><td>No insurer on the record</td><td className="n"><b>{countOf(r.notReady.noCarrier)}</b></td></tr>
                <tr><td>A placeholder, not an insurer (&quot;Unknown&quot;, &quot;Pending&quot;, &quot;Do not call&quot;)</td><td className="n"><b>{countOf(r.notReady.placeholder)}</b></td></tr>
                <tr><td>The insurer field names a person, a doctor, a health plan or a lien holder</td><td className="n"><b>{countOf(r.notReady.notInsurer)}</b></td></tr>
                <tr><td>No real policy number (blank, &quot;Unknown&quot;, &quot;N/A&quot;, &quot;000&quot;)</td><td className="n"><b>{countOf(r.notReady.noPolicy)}</b></td></tr>
                <tr><td>Its side of the case is already opened on another record (a duplicate, or a second at-fault driver)</td><td className="n"><b>{countOf(r.notReady.sideOpened)}</b></td></tr>
                <tr><td>Duplicate record: same case, side and policy as another ready record</td><td className="n"><b>{countOf(r.notReady.duplicate)}</b></td></tr>
              </tbody></table>
            </div>
          </div>
          <div className="tr-note mt">Ready = a client (PLT) or at-fault (DEF) record with no real claim number, on an open or litigation auto case (main clients only), naming a real insurer and a real policy number, whose side of the case no other record has opened, and that is not a second record of the same case, side and policy as another ready one (a duplicate counts once). Each day is rebuilt from Salesforce&apos;s field history (claim number, insurer, policy number, case status); the side, the insurer&apos;s account type and the second-client and test flags are today&apos;s. Claim numbers added is the 5:30 pm email&apos;s count, which also counts records that were never ready (no policy on file).</div>
        </>
      ) : <div className="tr-na">Not available right now.</div>}
    </section>
  );
}

function SourcesSection({ t, q }: { t: Trends; q: string }) {
  const rows = (t.sources ?? []).filter((s) => matchesWords([s.label], searchWordsOf(q)));
  return (
    <section className="tcard tr-card" aria-labelledby="tr-src">
      <div className="tr-h"><h2 id="tr-src" className="sc-h">AI claim calls by who started them, last 30 days</h2></div>
      {t.sources ? (
        <div className="tr-tw"><table className="tr-table">
          <thead><tr><th>Started by</th><th className="n">Requests</th><th className="n">Calls placed</th><th className="n">Claim numbers</th><th className="n" title="Claim numbers per call placed">Got a claim #</th><th>Requests per day</th></tr></thead>
          <tbody>{rows.map((s) => (
            <tr key={s.source}><td>{s.label}</td><td className="n"><b>{s.requests}</b></td><td className="n">{s.placed}</td><td className="n">{s.claims}</td>
              <td className="n">{pct(s.claims, s.placed)}</td><td><Spark values={s.daily} label={s.label} /></td></tr>
          ))}</tbody>
        </table>{!rows.length && t.sources.length && searchWordsOf(q).length ? <div className="tr-na">{SEARCH_WORDS.none(q)}</div> : null}</div>
      ) : <div className="tr-na">Not available right now.</div>}
      <div className="tr-note mt">The Today page's rule, one day at a time. Claim numbers = calls that came back with one.</div>
    </section>
  );
}

function NeedsSection({ t, q }: { t: Trends; q: string }) {
  const rows = (t.needs ?? []).filter((n) => matchesWords([n.label], searchWordsOf(q)));
  const max = Math.max(1, ...rows.map((n) => n.open));
  return (
    <section className="tcard tr-card" aria-labelledby="tr-needs">
      <div className="tr-h"><h2 id="tr-needs" className="sc-h">Most common "needs you" tasks, open now</h2></div>
      {t.needs ? (rows.length ? (
        <div className="tr-tw"><table className="tr-table">
          <thead><tr><th>What to do</th><th className="n">Open</th><th /><th className="n">New in 7 days</th><th className="n">Longest wait</th><th className="n">Typical wait</th></tr></thead>
          <tbody>{rows.map((n) => (
            <tr key={n.key}><td>{n.label}</td><td className="n"><b>{n.open}</b></td><td className="tr-barc"><i className="tr-bar" style={{ width: `${(100 * n.open) / max}%` }} /></td>
              <td className="n">{n.newIn7}</td><td className="n">{daysWord(n.oldestDays)}</td><td className="n">{daysWord(n.medianDays)}</td></tr>
          ))}</tbody>
        </table></div>) : <div className="tr-na">{searchWordsOf(q).length && t.needs.length ? SEARCH_WORDS.none(q) : 'Nothing needs a person right now.'}</div>) : <div className="tr-na">Not available right now.</div>}
      <div className="tr-note mt">The Open issues rows, grouped as the Matters page groups them. Typical wait = the middle row's wait.</div>
    </section>
  );
}

export function FeedbackSection({ t, q }: { t: Trends; q: string }) {
  const f = t.feedback;
  const rows = (f?.rows ?? []).filter((r) => matchesWords([r.label, r.text, ...r.carriers.map((c) => c.name)], searchWordsOf(q)));
  return (
    <section className="tcard tr-card" aria-labelledby="tr-fb">
      <div className="tr-h"><h2 id="tr-fb" className="sc-h">Carrier feedback: why dialed AI calls failed</h2></div>
      {f ? (
        <>
          <div className="tr-sum"><span><b>{f.dialedMisses7}</b> failed calls in the last 7 days</span><span><b>{f.dialedMisses30}</b> in 30 days</span></div>
          <div className="tr-tw"><table className="tr-table">
            <thead><tr><th>Why</th><th className="n">Last 7 days</th><th className="n">Last 30 days</th><th className="n">Share</th><th>Most often (30 days)</th><th>Per day</th></tr></thead>
            <tbody>{rows.map((r) => (
              <tr key={r.key}><td title={r.text}>{(() => {
                let i = r.daily.length - 1;
                while (i >= 0 && !(r.daily[i]! > 0)) i--;
                const d = i >= 0 ? t.days[i] : undefined;
                return d ? <Link to={todayHref(d.day, { result: 'miss', reason: r.key }, d.partial)} title="Open the latest day with these misses on Today">{r.label}</Link> : r.label;
              })()}</td><td className="n"><b>{r.last7}</b></td><td className="n">{r.last30}</td><td className="n">{pct(r.last30, f.dialedMisses30)}</td>
                <td className="tr-car">{r.carriers.length ? r.carriers.map((c) => `${c.name} ${c.n}`).join(' · ') : <span className="mt">–</span>}</td>
                <td><Spark values={r.daily} label={r.label} /></td></tr>
            ))}</tbody>
          </table>{!rows.length && (f.rows ?? []).length && searchWordsOf(q).length ? <div className="tr-na">{SEARCH_WORDS.none(q)}</div> : null}</div>
        </>
      ) : <div className="tr-na">Not available right now.</div>}
      <div className="tr-note mt">Calls that reached a carrier and came back without a claim number, sorted by the 5:30 pm email's reasons. Requests stopped before dialing are not here.</div>
    </section>
  );
}

function FactsSection({ t }: { t: Trends }) {
  const f = t.facts;
  const sf = f?.stateFarm ?? null;
  return (
    <section className="tcard tr-card" aria-labelledby="tr-facts">
      <div className="tr-h"><h2 id="tr-facts" className="sc-h">Facts to collect</h2></div>
      {f ? (
        <div className="tr-two">
          <div>
            <h3 className="tr-sub">What the open issues ask a person to type first</h3>
            {f.kinds.length ? <table className="tr-table compact"><tbody>{f.kinds.map((k) => <tr key={k.kind}><td>{k.label}</td><td className="n"><b>{k.open}</b></td></tr>)}</tbody></table>
              : <div className="tr-na">Nothing open.</div>}
          </div>
          <div>
            <h3 className="tr-sub">State Farm online filing</h3>
            {sf ? (
              <>
                <div className="tr-sum"><span><b>{sf.open}</b> open requests</span><span>{sf.needsAnswers} missing a fact · {sf.blocked} blocked</span></div>
                {sf.fields.length ? <table className="tr-table compact"><thead><tr><th>Missing fact</th><th className="n">Requests</th></tr></thead>
                  <tbody>{sf.fields.map((x) => <tr key={x.key}><td>{x.label}</td><td className="n"><b>{x.n}</b></td></tr>)}</tbody></table> : null}
                {sf.blockers.length ? <table className="tr-table compact"><thead><tr><th>Blocked because</th><th className="n">Requests</th></tr></thead>
                  <tbody>{sf.blockers.map((x) => <tr key={x.key}><td>{x.label}</td><td className="n"><b>{x.n}</b></td></tr>)}</tbody></table> : null}
              </>
            ) : <div className="tr-na">Not available right now.</div>}
          </div>
        </div>
      ) : <div className="tr-na">Not available right now.</div>}
    </section>
  );
}

export function TrendsPage() {
  const [q0, setQ] = useState('');
  // the typed words as typed (core list_search.ts reads them; the no-match line quotes them)
  const q = q0;
  const d = useQuery({ queryKey: ['trends'], queryFn: api.trends, staleTime: 5 * 60_000, refetchInterval: 15 * 60_000 });
  const t = d.data;
  const asOf = useMemo(() => (t?.asOf ? t.asOf : null), [t]);
  return (
    <Shell>
      <Topbar title="Trends" sub="the last 30 days" asOf={asOf} q={q0} setQ={setQ} ph={FRAME_SEARCH.trends} stale={d.isRefetchError} onRetry={() => d.refetch()} />
      <div className="content tight tr-page shadcn">
        <QueryState q={d} what="the 30-day trends" loading="Loading 30 days of claim numbers and calls (up to a minute the first time)." />
        {t ? (
          <>
            <ReadySection t={t} />
            <AddedSection t={t} />
            <NeedsSection t={t} q={q} />
            <FeedbackSection t={t} q={q} />
            <SourcesSection t={t} q={q} />
            <FactsSection t={t} />
            {t.notes.length ? <div className="tcard tr-notes">{t.notes.map((n) => <div key={n}>{n}</div>)}{asOf ? <div className="mt">Read {pacificStamp(asOf)} Pacific; refreshed every 15 minutes.</div> : null}</div>
              : asOf ? <div className="tr-notes mt">Read {pacificStamp(asOf)} Pacific; refreshed every 15 minutes.</div> : null}
          </>
        ) : null}
      </div>
    </Shell>
  );
}
